import * as THREE from 'three';

import { getColorScheme, getGlobalState } from '@gui/store/persistent/persistentStore.js';
import { createLinesMesh, createMesh } from '../../../Graphics/standardRenderer';
import FVector from '@data/geometry/FVector';
import Triangle from '@data/geometry/Triangle';
import { getViewer } from 'store/viewerStore';

export class VertexAttributes {
    pntCount: number;
    indCount: number;
    indices: Uint32Array;
    triID: Uint32Array;
    position: { data: Float32Array };
    triangleColor: number;
    strokeColor: number;
    constructor(copyFrom?: VertexAttributes) {
        if (copyFrom) {
            this.pntCount = copyFrom.pntCount;
            this.indCount = copyFrom.indCount;
            this.indices = copyFrom.indices.slice();
            this.triID = copyFrom.triID.slice();
            this.position = { data: copyFrom.position.data.slice() };
            this.triangleColor = copyFrom.triangleColor;
            this.strokeColor = copyFrom.strokeColor;
        } else {
            this.pntCount = 0;
            this.indCount = 0;
            this.indices = new Uint32Array();
            this.triID = new Uint32Array();
            this.position = { data: new Float32Array() };
            this.triangleColor = 0;
            this.strokeColor = 0;
        }
    }
}

export abstract class GeoObject {
    name: string;
    layerName: string[];
    trianglesMesh: THREE.Group = new THREE.Group();
    strokesMesh: THREE.Group = new THREE.Group();

    selectedMesh: THREE.Mesh = new THREE.Mesh();
    vertAttr: VertexAttributes[];
    colorStroke: number[];
    material: THREE.MeshBasicMaterial[];
    shadowMaterial: THREE.MeshLambertMaterial[];

    constructor() {
        this.name = 'Generic';
        this.layerName = [];
        this.vertAttr = [];
        this.colorStroke = [];
        this.shadowMaterial = [];
        this.material = [];
    }

    abstract isVisible(): boolean;

    static createVertexAttributesSimple() {
        return {
            pntCount: 0,
            indCount: 0,
            indices: new Uint32Array(),
            triID: new Uint32Array(),
            position: {
                data: new Float32Array(),
            },
            triangleColor: getColorScheme().GROUND,
            strokeColor: getColorScheme().GROUND_STROKE,
        };
    }

    /**
     * Initializes the mesh of the ground in the given Three.js scene by adding the mesh to the scenegraph when the data has finished loading.
     *
     */
    initMeshes(): { trianglesMesh: THREE.Group; strokesMesh: THREE.Group } {
        this.trianglesMesh = new THREE.Group();
        this.strokesMesh = new THREE.Group();

        for (let i = 0; i < this.vertAttr.length; i++) {
            const mesh = createMesh(this.vertAttr[i].position.data, [...this.vertAttr[i].indices], this.material[i]);
            const meshWire = createLinesMesh(this.vertAttr[i].position.data, [...this.vertAttr[i].indices], this.colorStroke[i]);
            this.trianglesMesh.add(mesh);
            this.strokesMesh.add(meshWire);
        }

        // Add meshes and strokes to the scene:
        this.trianglesMesh.name = this.name + 'triangles';
        this.strokesMesh.name = this.name + 'strokes';

        return { trianglesMesh: this.trianglesMesh, strokesMesh: this.strokesMesh };
    }

    dispose() {
        const scene: THREE.Scene = getViewer().scene;
        if (this.trianglesMesh && this.trianglesMesh.children.length > 0) {
            scene.remove(this.trianglesMesh);
            this.trianglesMesh.traverse((child) => {
                const mesh = child as THREE.Mesh;
                const geometry = (mesh as any).geometry;
                if (geometry?.dispose) geometry.dispose();
                const material = (mesh as any).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat?.dispose?.());
                } else {
                    material?.dispose?.();
                }
            });
            this.trianglesMesh.clear();
        }
        if (this.strokesMesh && this.strokesMesh.children.length > 0) {
            scene.remove(this.strokesMesh);
            this.strokesMesh.traverse((child) => {
                const mesh = child as THREE.Mesh;
                const geometry = (mesh as any).geometry;
                if (geometry?.dispose) geometry.dispose();
                const material = (mesh as any).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat?.dispose?.());
                } else {
                    material?.dispose?.();
                }
            });
            this.strokesMesh.clear();
        }
    }

    abstract updateColors(): void;

    toggleVisibility() {
        const { ambient } = getGlobalState();
        const isRenderMode = ambient.mode === 'render';
        const showStrokes = ambient.showStrokes;

        const isFillVisible = this.isVisible();
        const areStrokesVisible = this.isVisible() && showStrokes && !isRenderMode;

        // Activate / Deactivate:
        if (this.trianglesMesh) this.trianglesMesh.visible = isFillVisible;

        if (this.strokesMesh) this.strokesMesh.visible = areStrokesVisible;
        // console.log(isFillVisible, areStrokesVisible);
    }
}

// exported function because Serialization needs it
export function processVertexAttributesAndIndices(part: any, points: any[], triangles: any[], flip: boolean = true) {
    part.pntCount = points.length;
    part.indCount = triangles.length * 3;
    part.indices = new Uint32Array(part.indCount);
    part.triID = new Uint32Array(part.indCount);
    part.position = { data: new Float32Array(part.pntCount * 3) };

    // Populate position data using a single loop
    points.forEach((point, index) => {
        part.position.data.set([point.x, point.y, point.z], index * 3);
    });

    // Populate indices and triID using a single loop
    triangles.forEach((triangle, index) => {
        part.indices.set([triangle.A, flip ? triangle.C : triangle.B, flip ? triangle.B : triangle.C], index * 3);
        part.triID.set([triangle.ID, triangle.ID, triangle.ID], index * 3);
    });
}

export function getGeometryFromRawData(points: FVector[], triangles: Triangle[], meshColor: THREE.Color): THREE.Mesh {
    const pointCount = points.length;

    let indices: number[] = [];
    const vertices = new Float32Array(pointCount * 3);
    points.forEach((p, idx) => {
        vertices.set([p.x, p.y, p.z], idx * 3);
    });
    triangles.forEach((tri) => {
        indices.push(tri.A);
        indices.push(tri.C);
        indices.push(tri.B);
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    //geometry.computeVertexNormals();

    let material = new THREE.MeshBasicMaterial({
        color: meshColor,
        side: THREE.DoubleSide,
        polygonOffset: true,
        depthTest: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
    });

    let mesh = new THREE.Mesh(geometry, material);
    return mesh;
}

export function getOnlyGeometryFromRawData(points: FVector[], triangles: Triangle[]): THREE.BufferGeometry {
    const pointCount = points.length;

    let indices: number[] = [];
    const vertices = new Float32Array(pointCount * 3);
    points.forEach((p, idx) => {
        vertices.set([p.x, p.y, p.z], idx * 3);
    });
    triangles.forEach((tri) => {
        indices.push(tri.A);
        indices.push(tri.C);
        indices.push(tri.B);
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    //geometry.computeVertexNormals();

    return geometry;
}
